// src/features/dashboard/components/DashboardStatsCard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Hooks
import { useTasks } from '../../tasks/hooks/useTasks';
import useCachedData from '../hooks/useCachedData';
import { CACHE_KEYS } from '../utils/dashboardCache';
import { statCardGradientSx } from '../../../theme/statCardStyle';

const StatCard = React.memo(function StatCard({ 
  title, 
  value, 
  color, 
  icon: Icon, 
  trend = null,
  loading = false 
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <Card
      sx={{
        height: '100%',
        overflow: 'visible',        // <-- FIX: gradient isn't clipped anymore
        backgroundColor: 'transparent',  // remove MUI default paper bg
        ...statCardGradientSx(color),
      }}
    >
      <CardContent sx={{ 
        p: isMobile ? 2 : 3,
        '&:last-child': { pb: isMobile ? 2 : 3 }
      }}>
        {isMobile ? (
          // Mobile Layout: Horizontal with icon and text side by side
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                borderRadius: 2,
                backgroundColor: color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                {loading ? (
                  <>
                    <CircularProgress size={12} sx={{ color }} />
                    <Typography variant="body2" fontWeight="bold" color={color}>
                      --
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" fontWeight="bold" sx={{ color }}>
                    {value}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" fontWeight="medium" noWrap>
                  {title}
                </Typography>
              </Box>
              
              {trend && !loading && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend.positive ? 'success.main' : 'error.main',
                    fontWeight: 'medium',
                    fontSize: '0.7rem'
                  }}
                >
                  {trend.positive ? '↗' : '↘'} {trend.text}
                </Typography>
              )}
            </Stack>
          </Box>
        ) : (
          // Desktop Layout: Original vertical layout
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box
                sx={{
                  borderRadius: 2,
                  backgroundColor: color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5,
                }}
              >
                <Icon sx={{ fontSize: 24 }} />
              </Box>
            </Box>

            <Stack spacing={1}>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color }} />
                  <Typography variant="h4" fontWeight="bold" color={color}>
                    --
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h4" fontWeight="bold" sx={{ color }}>
                  {value}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                {title}
              </Typography>
              
              {trend && !loading && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend.positive ? 'success.main' : 'error.main',
                    fontWeight: 'medium'
                  }}
                >
                  {trend.positive ? '↗' : '↘'} {trend.text}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
});

const DashboardStatsCards = React.memo(function DashboardStatsCards({
  autoRefresh = true,
  refreshInterval = 300000, // 5 minutes
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // API hooks
  const { fetchTaskStatistics, filterTasks } = useTasks();

  // Cached data for statistics
  const {
    data: statsData,
    loading: statsLoading,
    isBackgroundUpdate: statsBackgroundUpdate
  } = useCachedData(
    CACHE_KEYS.STATS,
    fetchTaskStatistics,
    { autoRefresh, refreshInterval }
  );

  // Cached data for today's completed tasks
  const {
    data: todayCompletedData,
    loading: todayLoading,
    isBackgroundUpdate: todayBackgroundUpdate
  } = useCachedData(
    CACHE_KEYS.TODAY_COMPLETED,
    useCallback(async () => {
      const today = new Date().toISOString().split('T')[0];
      return await filterTasks({
        date_field: 'completion_date',
        start_date: today,
        end_date: today,
        states: ['COMPLETED']
      });
    }, [filterTasks]),
    { autoRefresh, refreshInterval }
  );

  // Extract the actual data
  const stats = statsData?.data || null;
  const todayCompleted = todayCompletedData?.data || null;

  // Calculate completion rate trend
  const getCompletionTrend = () => {
    if (!stats) return null;
    
    const rate = stats.completion_rate || 0;
    if (rate >= 80) {
      return { positive: true, text: t('common:dashboard.stats.trends.excellent_performance') };
    } else if (rate >= 60) {
      return { positive: true, text: t('common:dashboard.stats.trends.good_progress') };
    } else {
      return { positive: false, text: t('common:dashboard.stats.trends.needs_attention') };
    }
  };

  const statisticsCards = [
    {
      title: t('common:dashboard.stats.active_tasks'),
      value: stats ? (stats.pending_tasks + stats.in_progress_tasks) : '--',
      color: theme.palette.secondary.main,
      icon: AssignmentIcon,
      trend: stats ? { 
        positive: (stats.pending_tasks + stats.in_progress_tasks) <= stats.total_tasks * 0.7,
        text: t('common:dashboard.stats.trends.pending_active', {
          pending: stats.pending_tasks,
          active: stats.in_progress_tasks
        })
      } : null,
    },
    {
      title: t('common:dashboard.stats.completed_today'),
      value: todayCompleted ? todayCompleted.total_count : '--',
      color: theme.palette.success.main,
      icon: CheckCircleIcon,
      trend: todayCompleted && todayCompleted.total_count > 0 ? {
        positive: true,
        text: t('common:dashboard.stats.trends.of_total_tasks', {
          percent: ((todayCompleted.total_count / (stats?.total_tasks || 1)) * 100).toFixed(0)
        })
      } : null,
    },
    {
      title: t('common:dashboard.stats.in_progress'),
      value: stats ? stats.in_progress_tasks : '--',
      color: theme.palette.info.main,
      icon: PlayArrowIcon,
      trend: stats && stats.in_progress_tasks > 0 ? {
        positive: stats.in_progress_tasks <= stats.total_tasks * 0.3,
        text: t('common:dashboard.stats.trends.of_total', {
          percent: ((stats.in_progress_tasks / stats.total_tasks) * 100).toFixed(0)
        })
      } : null,
    },
    {
      title: t('common:dashboard.stats.overdue'),
      value: stats ? stats.overdue_tasks : '--',
      color: theme.palette.error.main,
      icon: WarningIcon,
      trend: stats ? {
        positive: stats.overdue_tasks === 0,
        text: stats.overdue_tasks === 0 
          ? t('common:dashboard.stats.trends.all_on_track')
          : t('common:dashboard.stats.trends.requires_attention')
      } : null,
    },
  ];

  // Show loading only on initial load, not during background updates
  const isInitialLoading = statsLoading && !stats;
  const showBackgroundIndicator = statsBackgroundUpdate || todayBackgroundUpdate;

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {statisticsCards.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            color={stat.color}
            icon={stat.icon}
            trend={stat.trend}
            loading={isInitialLoading}
          />
        ))}
      </Box>

      {/* Add keyframe animation for refresh icon */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
});

export default DashboardStatsCards;